import { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

/**
 * The clustered dealer map the Stellantis dashboards share.
 *
 * react-leaflet owns the map and the tile layer, but the markers are built with
 * the Leaflet API directly rather than as `<Marker>` children. Two reasons, both
 * about the cluster: `leaflet.markercluster` wants to be handed a layer group it
 * manages itself, and a wave runs to thousands of markers, which as React
 * elements would mean thousands of components and popups mounted to draw a
 * screen showing a few dozen pins.
 *
 * Popups are therefore HTML strings, as they were in the Razor versions, and are
 * built lazily so only the one actually opened costs anything.
 */

export interface MapMarker {
  /** Stable identity; only used to key the caller's own bookkeeping. */
  id: string;
  latitude: number;
  longitude: number;
  /** Fill of the circle pin. */
  color: string;
  /** Diameter in pixels. */
  size: number;
  /** Built when the popup opens, not when the marker is created. */
  popupHtml: () => string;
  /** Read back by `clusterColor` to tint the cluster bubble. */
  categoryIndex?: number;
}

export interface ClusterMapProps {
  markers: MapMarker[];
  center: L.LatLngExpression;
  zoom: number;
  minZoom?: number;
  maxZoom?: number;
  /**
   * Colour for a cluster, given the category most of its members are in. Omit to
   * keep `leaflet.markercluster`'s own green/yellow/red count bubbles.
   */
  clusterColor?: (categoryIndex: number) => string;
  /**
   * Zoom to fit the markers after every update. Off by default: a map that
   * re-frames itself on each filter change is disorienting unless, as on the
   * Takata screen, the filters are how you navigate it.
   */
  fitToMarkers?: boolean;
  /**
   * Changes whenever something outside the map resizes it — a collapsed sidebar,
   * an expanded table panel. Leaflet caches the container size and cannot observe
   * a CSS transition, so it has to be told.
   */
  resizeToken?: unknown;
}

/** Escapes a value for interpolation into popup markup. */
export function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function ClusterLayer({
  markers,
  clusterColor,
  fitToMarkers,
}: Pick<ClusterMapProps, 'markers' | 'clusterColor' | 'fitToMarkers'>) {
  const map = useMap();
  const clusterRef = useRef<L.MarkerClusterGroup | null>(null);

  const cluster = useMemo(() => {
    const options: L.MarkerClusterGroupOptions = {
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
    };

    // The bubble takes its colour from the category most of its members are in,
    // so a cluster over a struggling region reads red without expanding it.
    if (clusterColor) {
      options.iconCreateFunction = (marker) => {
        const children = marker.getAllChildMarkers();
        const counts = new Map<number, number>();

        for (const child of children) {
          const index = (child.options as { categoryIndex?: number }).categoryIndex ?? 0;
          counts.set(index, (counts.get(index) ?? 0) + 1);
        }

        let prevalent = 0;
        let highest = 0;
        for (const [index, count] of counts) {
          if (count > highest) {
            highest = count;
            prevalent = index;
          }
        }

        return L.divIcon({
          html: `<div style="background-color: ${clusterColor(prevalent)}; color: white; border-radius: 50%;
                             width: 40px; height: 40px; display: flex; align-items: center;
                             justify-content: center; font-weight: bold; border: 3px solid white;
                             box-shadow: 0 2px 5px rgba(0,0,0,0.3);">
                    ${children.length}
                 </div>`,
          className: 'marker-cluster-custom',
          iconSize: L.point(40, 40),
        });
      };
    }

    return L.markerClusterGroup(options);
  }, [clusterColor]);

  useEffect(() => {
    clusterRef.current = cluster;
    map.addLayer(cluster);
    return () => {
      map.removeLayer(cluster);
      clusterRef.current = null;
    };
  }, [map, cluster]);

  useEffect(() => {
    if (clusterRef.current !== cluster) return;

    cluster.clearLayers();

    for (const marker of markers) {
      // A dealer the geocoder never resolved sits at 0,0 — in the Atlantic. The
      // Razor versions skipped those rather than plotting them there.
      if (!marker.latitude || !marker.longitude) continue;

      const layer = L.marker([marker.latitude, marker.longitude], {
        icon: L.divIcon({
          className: 'custom-marker',
          html: `<div style="background-color: ${marker.color}; width: ${marker.size}px; height: ${marker.size}px;
                             border-radius: 50%; border: 2px solid white;
                             box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>`,
          iconSize: [marker.size, marker.size],
          iconAnchor: [marker.size / 2, marker.size / 2],
        }),
        categoryIndex: marker.categoryIndex,
      } as L.MarkerOptions);

      layer.bindPopup(marker.popupHtml);
      cluster.addLayer(layer);
    }

    if (fitToMarkers && markers.length > 0) {
      const bounds = cluster.getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [map, cluster, markers, fitToMarkers]);

  return null;
}

/** Tells Leaflet to re-measure after the surrounding layout changed. */
function ResizeOnToken({ token }: { token: unknown }) {
  const map = useMap();

  useEffect(() => {
    // After the CSS transition that changed the size, not during it.
    const timer = window.setTimeout(() => map.invalidateSize(), 350);
    return () => window.clearTimeout(timer);
  }, [map, token]);

  return null;
}

export function ClusterMap({
  markers,
  center,
  zoom,
  minZoom,
  maxZoom,
  clusterColor,
  fitToMarkers = false,
  resizeToken,
}: ClusterMapProps) {
  return (
    <MapContainer
      id="regionalMap"
      center={center}
      zoom={zoom}
      minZoom={minZoom}
      maxZoom={maxZoom}
      scrollWheelZoom
      zoomControl
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        maxZoom={18}
      />
      <ClusterLayer markers={markers} clusterColor={clusterColor} fitToMarkers={fitToMarkers} />
      <ResizeOnToken token={resizeToken} />
    </MapContainer>
  );
}
